/* eslint-disable react/jsx-no-bind */
import { useState, useMemo, useCallback, FC, useEffect } from "react";
import cn from "classnames";
import EmojiAvatar from "~/components/shared/EmojiAvatar";
import Combox from "~/components/shared/Combox";
import ComboxTrigger from "~/components/shared/Combox/ComboxTrigger";
import ComboxPopover from "~/components/shared/Combox/ComboxPopover";
import ColorPickerSection from "~/components/shared/NavatarPicker/ColorPickerSection";
import EmojiGridSection from "~/components/shared/NavatarPicker/EmojiGridSection";
import FooterSection from "~/components/shared/NavatarPicker/FooterSection";
import {
  useProcessedEmojis,
  getEmojiSrc,
  findEmojiByName,
  type ProcessedEmoji,
  type EmojiData,
} from "~/lib/emoji-utils";

export const EMOJI_CONFIG = {
  ITEM_HEIGHT: 40, // Height of each emoji item including gap
  OVERSCAN: 10, // Number of items to render outside visible area
};

export const LIGHT_COLORS = [
  "#FFDAB9",
  "#FFEFD5",
  "#FFFACD",
  "#CCEFAC",
  "#F0F8FF",
  "#BCE3FF",
  "#EAE9FF",
  "#F5C5FE",
  "#F5F5F5",
];
export const DARK_COLORS = [
  "#854444",
  "#A77048",
  "#D1B46A",
  "#34604B",
  "#2D4468",
  "#0062A8",
  "#4B3B58",
  "#6F304A",
  "#292929",
];

export { getEmojiSrc, type ProcessedEmoji, type EmojiData };

export interface NavatarPickerProps {
  value?: string | null;
  color?: string | null;
  onEmojiChange?: (emoji: string | null) => void;
  onColorChange?: (color: string | null) => void;
  className?: string;
  size?: "sm" | "base" | "lg" | number;
  testId?: string;
}

const useEmojiPreloader = (emojis: ProcessedEmoji[]) => {
  const preload = useCallback(() => {
    // Preload first 50 emojis for instant display
    emojis
      .slice(0, 50)
      .filter((emoji) => emoji.image)
      .forEach((emoji) => {
        const src = getEmojiSrc(emoji);
        const img = new window.Image();
        img.loading = "eager";
        img.src = src;
      });
  }, [emojis]);
  return { preload };
};

const NavatarPicker: FC<NavatarPickerProps> = ({
  value,
  color,
  onEmojiChange,
  onColorChange,
  className,
  size = "base",
  testId = "navatar-picker",
}) => {
  // The popover open state
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredEmoji, setHoveredEmoji] = useState<ProcessedEmoji | null>(null);
  const [selectedColor, setSelectedColor] = useState(color || "#F5F5F5");
  const [selectedEmoji, setSelectedEmoji] = useState(value);

  const allEmojis = useProcessedEmojis();
  const { preload } = useEmojiPreloader(allEmojis);

  const PRESET_COLORS = useMemo(() => [...LIGHT_COLORS, ...DARK_COLORS], []);
  const isCustomColor = useMemo(
    () => !PRESET_COLORS.includes(selectedColor || ""),
    [PRESET_COLORS, selectedColor],
  );

  // Preload the images for first 70 on mount
  useEffect(() => {
    preload();
  }, [preload]);

  const handleEmojiSelect = useCallback(
    (emoji: ProcessedEmoji) => {
      const emojiValue = `:${emoji.name}:`;
      setSelectedEmoji(emojiValue);
      onEmojiChange?.(emojiValue);
      setIsOpen(false);
    },
    [onEmojiChange],
  );

  const handleColorSelect = useCallback(
    (newColor: string) => {
      setSelectedColor(newColor);
      onColorChange?.(newColor);
    },
    [onColorChange],
  );

  const displayEmoji: ProcessedEmoji | null =
    hoveredEmoji || (selectedEmoji && findEmojiByName(allEmojis, selectedEmoji)) || null;

  return (
    <div className={cn("inline-block", className)} data-testid={testId}>
      <Combox
        value={selectedEmoji || ""}
        onValueChange={(emojiValue) => {
          const emoji = findEmojiByName(allEmojis, emojiValue);
          if (emoji) {
            handleEmojiSelect(emoji);
          }
        }}
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <ComboxTrigger
          className={cn(
            "border-1 focus:outline-hidden -mt-px w-fit gap-0 border-white !p-0 shadow-none ring-1 hover:shadow-none hover:ring-gray-600",
            isOpen ? "ring-gray-700" : "ring-transparent",
          )}
          data-testid={`${testId}-trigger`}
          disclosure={false}
        >
          <EmojiAvatar
            emoji={selectedEmoji}
            color={selectedColor}
            size={size}
            className="cursor-pointer"
          />
        </ComboxTrigger>

        {/* Popover is force mounted for instant display */}
        <ComboxPopover
          forceMount={true}
          className={cn(
            "relative w-[340px] rounded-lg border border-gray-200 bg-white p-0 shadow-lg transition-opacity duration-0",
            isOpen ? "visible opacity-100" : "pointer-events-none invisible opacity-0",
          )}
        >
          <ColorPickerSection
            selectedColor={selectedColor}
            isCustomColor={isCustomColor}
            onColorSelect={handleColorSelect}
            onEscape={() => setIsOpen(false)}
          />

          <EmojiGridSection
            emojis={allEmojis}
            selectedEmoji={selectedEmoji || null}
            onHoveredEmojiChange={setHoveredEmoji}
            getEmojiSrc={getEmojiSrc}
            isOpen={isOpen}
          />

          <FooterSection displayEmoji={displayEmoji} />
        </ComboxPopover>
      </Combox>
    </div>
  );
};

export default NavatarPicker;
